import React, { useState, useEffect, useRef } from 'react';
import { Category, Subject, Degree, FormState } from '../types';
import tagularEvent from '@/utils/tagular/tagularEvent';
import uuid from '@/utils/uuid';
import { FieldSelected } from '@/types/Tagular';
import numberFormatter from '@/utils/numberFormatter';
interface EducationSelectionPanelProps {
  selectedDegree: string;
  selectedCategory: string;
  selectedSubject: string;
  inputtedSalary: number | null;
  setSelectedDegree: (slug: string) => void;
  setSelectedCategory: (slug: string) => void;
  setSelectedSubject: (slug: string) => void;
  setSubjects: (subjects: Subject[]) => void;
  setInputtedSalary: (salary: number | null) => void;
  fireInputChange?: (fieldName: string, value: string | number) => void;
  handleFieldChange?: (fieldName: string, value: string) => void;
  presetDegreeSlug?: string;
  presetCategorySlug?: string;
  presetSubjectSlug?: string;
  formState: FormState;
  initialValues?: {
    degree?: string | number;
    category?: string | number;
    subject?: string | number;
  };
}

const EducationSelectionPanel: React.FC<EducationSelectionPanelProps> = ({
  selectedDegree,
  selectedCategory,
  selectedSubject,
  inputtedSalary,
  setSelectedDegree,
  setSelectedCategory,
  setSelectedSubject,
  setSubjects,
  setInputtedSalary,
  fireInputChange,
  handleFieldChange,
  formState,
}) => {
  const { currencyFormat } = numberFormatter();
  // Local state for salary since that's not managed by useDynamicDropdowns
  // const [salary, setSalary] = useState<number | null>(inputtedSalary);
  const previousSubject = useRef(formState.current.subject);

  // Update parent component state when local state changes
  useEffect(() => {
    // When formState changes, updated selected values
    if (formState.current.degree) {
      setSelectedDegree(formState.current.degree);
    }

    if (formState.current.category) {
      setSelectedCategory(formState.current.category);
    }

    if (formState.current.subject) {
      setSelectedSubject(formState.current.subject);
      previousSubject.current = formState.current.subject;

      if (formState?.options?.subject?.length > 0) {
        setSubjects(formState.options.subject);
      }
    }
  }, [
    formState,
    selectedDegree,
    selectedCategory,
    selectedSubject,
    setSelectedDegree,
    setSelectedCategory,
    setSelectedSubject,
    setSubjects,
  ]);
  // Keep local salary state in sync with prop
  // useEffect(() => {
  //   setSalary(inputtedSalary);
  // }, [inputtedSalary]);

  // Handle field selection and tracking
  const handleFieldSelect = (fieldName: string, value: string) => {
    // Use useDynamicDropdowns handleFieldChange for degree/category/subject
    if (
      ['degree', 'category', 'subject'].includes(fieldName) &&
      handleFieldChange
    ) {
      handleFieldChange(fieldName, value);

      if (fieldName === 'degree') {
        setSelectedDegree(value);
      } else if (fieldName === 'category') {
        // When category changes, also clear subject so pills reset
        setSelectedCategory(value);
        setSelectedSubject('');
      } else if (fieldName === 'subject') {
        setSelectedSubject(value);
      }

      // Find human-readable option name for tracking
      let optionName = '';
      const options =
        formState.options[fieldName as keyof typeof formState.options];
      const option = options.find((opt) => opt.slug === value);
      optionName = option?.name || '';

      // Track the field selection
      trackFieldSelection(fieldName, value, optionName);
    }
  };

  // Handle salary input changes
  const handleSalaryChange = (value: number) => {
    // setSalary(value === 0 ? null : value);
    setInputtedSalary(value === 0 ? null : value);
  };

  const handleSalaryBlur = () => {
    if (inputtedSalary && inputtedSalary > 0) {
      trackFieldSelection(
        'salary',
        inputtedSalary.toString(),
        inputtedSalary.toString(),
      );
    }
  };

  // Track field selections with Tagular
  const trackFieldSelection = (
    fieldName: string,
    value: string,
    optionName: string,
  ) => {
    if (fireInputChange) {
      fireInputChange(fieldName, value);
    } else {
      const correlationId = uuid();
      const fieldPayload: FieldSelected = {
        correlationId,
        formContext: {
          formId: '',
          formName: 'calculator DCS',
          formType: 'calculator',
          formVersion: '',
        },
        userInputField: {
          fieldName: fieldName,
          fieldType: fieldName,
          fieldValue: optionName,
        },
      };
      tagularEvent('FieldSelected', fieldPayload);
    }
  };

  return (
    <div className='col-span-1 bg-white rounded-lg w-full mb-4 md:mb-0'>
      <div className='font-bold font-body-14 mb-1'>
        Education & Career Settings
      </div>
      <p className='text-gray-600 font-body-12 mb-4'>
        Configure your education and career details
      </p>

      {/* Select Wrapper */}
      <div className='grid grid-cols-1 gap-2 md:gap-4'>
        <div>
          <label className='block font-bold font-body-14 mb-2' htmlFor='degree'>
            I Want My
          </label>
          <select
            id='degree'
            name='degree'
            className='w-full border border-gray-300 rounded p-2 font-body-14 truncate pr-6 ss-qdf-select'
            value={formState.current.degree}
            onChange={(e) => handleFieldSelect('degree', e.target.value)}
          >
            <option value='' disabled hidden>
              Select a degree
            </option>
            {formState.options.degree &&
              formState.options.degree.map((degree) => (
                <option
                  key={degree.id}
                  value={degree.slug}
                  className='font-body-14'
                >
                  {degree.name}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label
            className='block font-bold mb-2 font-body-14'
            htmlFor='category'
          >
            In
          </label>
          <select
            id='category'
            name='category'
            className='w-full border border-gray-300 rounded p-2 font-body-14 truncate pr-6 ss-qdf-select'
            value={formState.current.category}
            onChange={(e) => handleFieldSelect('category', e.target.value)}
            disabled={
              !formState.current.degree ||
              formState.options.category.length === 0
            }
          >
            <option value='' disabled hidden>
              Select a category
            </option>
            {formState.options.category &&
              formState.options.category.map((category) => (
                <option
                  key={category.id}
                  value={category.slug}
                  className='font-body-14'
                >
                  {category.name}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label
            className='block font-bold mb-2 font-body-14'
            htmlFor='subject'
          >
            Focusing On
          </label>
          <select
            id='subject'
            name='subject'
            className='w-full border border-gray-300 rounded p-2 font-body-14 truncate pr-6 ss-qdf-select'
            value={formState.current.subject}
            onChange={(e) => handleFieldSelect('subject', e.target.value)}
            disabled={
              !formState.current.category ||
              formState.options.subject.length === 0
            }
          >
            <option value='' disabled hidden>
              Select a subject
            </option>
            {formState.options.subject &&
              formState.options.subject.map((subject) => (
                <option
                  key={subject.id}
                  value={subject.slug}
                  className='font-body-14'
                >
                  {subject.name}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label
            className='block font-bold mb-2 font-body-14'
            htmlFor='currentSalary'
          >
            Current Salary
          </label>
          <div className='relative'>
            <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
              <span className='text-gray-500'>$</span>
            </div>
            <input
              id='currentSalary'
              name='currentSalary'
              type='text'
              className={`w-full rounded p-2 pl-7 font-body-14 ${
                !inputtedSalary || inputtedSalary <= 0
                  ? 'border-2 border-[#fca5a5] focus:border-[#ef4444] focus:ring-[#fecaca]'
                  : 'border border-gray-300 focus:border-primary-500 focus:ring-primary-200'
              } focus:outline-none focus:ring-2`}
              placeholder='Enter your current salary'
              value={
                inputtedSalary
                  ? currencyFormat?.format(inputtedSalary).replace('$', '')
                  : ''
              }
              onChange={(e) => {
                // Remove non-numeric characters and convert to number
                const value = e.target.value.replace(/[^0-9]/g, '');
                if (value === '') {
                  handleSalaryChange(0);
                } else {
                  handleSalaryChange(Number(value));
                }
              }}
              onBlur={(e) => {
                // Format on blur for better user experience
                if (inputtedSalary) {
                  e.target.value = currencyFormat
                    ?.format(inputtedSalary)
                    .replace('$', '');
                  handleSalaryBlur();
                }
              }}
            />
          </div>
          <p className={`text-xs mt-1 text-gray-500`}>
            Enter your annual salary before taxes
          </p>
        </div>
      </div>
    </div>
  );
};

export default EducationSelectionPanel;
